import React from 'react';
import { Info, Check, CheckCircle2, AlertCircle } from 'lucide-react';

export const SizeGuidePage = () => {
  return (
    <div className="bg-[#F9FAFB] min-h-screen py-12 px-4 md:px-8 font-sans">
      <div className="container mx-auto max-w-2xl">
        <div className="text-center mb-10">
             <h1 className="text-4xl md:text-5xl font-black italic text-[#061970] uppercase tracking-tighter mb-4">
                Guía de Tallas
            </h1>
            <p className="text-gray-500 font-medium">Encuentra el ajuste perfecto para tu seguridad.</p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-10 space-y-10">
            
            {/* Step 1: Mide Tu Cabeza */}
            <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md shadow-[#FF7F32]/20">1</div>
                <div className="space-y-4 pt-1 flex-1">
                    <div>
                        <h3 className="text-lg font-black text-[#061970] uppercase tracking-wide">MIDE TU CABEZA</h3>
                        <p className="text-sm text-gray-600 leading-relaxed mt-2">
                            Mide la circunferencia de tu cabeza. Pasa la cinta métrica por encima de las cejas y alrededor de la parte más ancha de tu cabeza.
                        </p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                        <div className="bg-gray-50 rounded-xl p-4 flex gap-4 items-start border border-gray-100 flex-1">
                            <Info className="w-5 h-5 text-[#459EDF] mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase mb-1">CONSEJO PRO</p>
                                <p className="text-sm text-gray-600 leading-snug">
                                    Si no tienes cinta métrica, usa un cordón y luego mídelo con una regla.
                                </p>
                            </div>
                        </div>
                        <div className="w-[128px] h-[159px] bg-gray-200 rounded-xl flex-shrink-0 border border-gray-200 mx-auto md:mx-0">
                            <img src='/guia_de_tallas/Cabeza_medidas.png'></img>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full h-px bg-gray-100" />

            {/* Step 2: Forma de la Cabeza */}
            <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md shadow-[#FF7F32]/20">2</div>
                <div className="space-y-5 pt-1 flex-1 w-full">
                    <div>
                        <h3 className="text-lg font-black text-[#061970] uppercase tracking-wide">FORMA DE LA CABEZA</h3>
                        <p className="text-sm text-gray-600 leading-relaxed mt-2">
                            Identifica la forma de tu cabeza (vista superior). Nuestros cascos están diseñados principalmente para formas <span className="font-bold text-gray-900">Intermedias</span>.
                        </p>
                    </div>
                    <div className="flex justify-between items-end px-4 sm:px-12 pt-4">
                        <div className="flex flex-col items-center gap-3 opacity-60">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-gray-300"></div>
                            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">REDONDA</span>
                        </div>
                        <div className="flex flex-col items-center gap-3 relative -top-2">
                            <div className="absolute -top-8 text-[#FF7F32]">
                                <div className="w-6 h-6 rounded-full border-2 border-[#FF7F32] bg-white flex items-center justify-center shadow-sm">
                                    <Check className="w-4 h-4" />
                                </div>
                            </div>
                            <div className="w-14 h-20 sm:w-16 sm:h-24 rounded-[30px] border-4 border-[#FF7F32] bg-[#FF7F32]/5 shadow-[0_0_20px_rgba(255,127,50,0.2)]"></div>
                            <span className="text-xs text-[#FF7F32] font-black uppercase tracking-wider">INTERMEDIA</span>
                        </div>
                        <div className="flex flex-col items-center gap-3 opacity-60">
                            <div className="w-20 h-14 sm:w-24 sm:h-16 rounded-[30px] border-2 border-gray-300"></div>
                            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">LARGA</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full h-px bg-gray-100" />

            {/* Step 3: Tabla de Medidas */}
            <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md shadow-[#FF7F32]/20">3</div>
                <div className="space-y-4 pt-1 flex-1 w-full overflow-hidden">
                    <h3 className="text-lg font-black text-[#061970] uppercase tracking-wide">TABLA DE MEDIDAS</h3>
                    <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm ring-1 ring-black/5">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 uppercase text-gray-500 font-bold border-b border-gray-200 text-xs tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 w-1/3">TALLA</th>
                                    <th className="px-4 py-3 w-1/3">PULGADAS</th>
                                    <th className="px-4 py-3 w-1/3">CENTÍMETROS</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {[
                                    { size: 'CHICO (S)', in: '21 1/2" - 22"', cm: '55 - 56 CM' },
                                    { size: 'MEDIANO (M)', in: '22 1/4" - 22 3/4"', cm: '57 - 58 CM' },
                                    { size: 'GRANDE (L)', in: '23" - 23 1/2"', cm: '59 - 60 CM' },
                                    { size: 'X-G (XL)', in: '23 3/4" - 24 1/2"', cm: '61 - 62 CM' },
                                    { size: 'XX-G (XXL)', in: '24 3/4" - 25 3/16"', cm: '63 - 64 CM' }
                                ].map((row) => (
                                    <tr key={row.size} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="px-4 py-3 font-bold text-[#061970] group-hover:text-[#FF7F32] transition-colors">{row.size}</td>
                                        <td className="px-4 py-3 text-gray-600 font-medium">{row.in}</td>
                                        <td className="px-4 py-3 font-bold text-[#FF7F32]">{row.cm}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="w-full h-px bg-gray-100" />

            {/* Step 4: Comprueba el Ajuste */}
            <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md shadow-[#FF7F32]/20">4</div>
                <div className="space-y-4 pt-1 flex-1">
                    <div>
                        <h3 className="text-lg font-black text-[#061970] uppercase tracking-wide">COMPRUEBA EL AJUSTE</h3>
                        <p className="text-sm text-gray-600 leading-relaxed mt-2">
                            El casco no debe moverse al girar la cabeza bruscamente.
                        </p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                        <div className="bg-[#459EDF]/5 rounded-xl p-4 flex gap-4 items-start border border-[#459EDF]/20 flex-1">
                            <Info className="w-5 h-5 text-[#459EDF] mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-xs font-bold text-[#061970] uppercase mb-1">Prueba de "Mejillas de Ardilla"</p>
                                <p className="text-sm text-gray-600 leading-snug mb-3">
                                    Debes sentir las almohadillas presionando ligeramente tus mejillas hacia arriba.
                                </p>
                                <div className="flex items-center gap-2 text-[10px] font-black text-[#459EDF] uppercase tracking-wider bg-white/50 px-2 py-1 rounded w-fit">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    <span>LAS ALMOHADILLAS CEDEN TRAS 15-20 HORAS</span>
                                </div>
                            </div>
                        </div>
                        <div className="w-[224px] h-[211px] bg-gray-200 rounded-xl flex-shrink-0 border border-gray-200 mx-auto md:mx-0">
                            <img src='/guia_de_tallas/Casco_medidas.png'></img>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Alert */}
            <div className="bg-[#FF7F32] rounded-xl p-4 flex items-center justify-center gap-3 text-white shadow-lg shadow-[#FF7F32]/20 mt-8 transform hover:scale-[1.02] transition-transform">
                <AlertCircle className="w-6 h-6" />
                <span className="text-sm md:text-base font-black uppercase tracking-wide">¡MIDE BIEN TU CABEZA ANTES DE ORDENAR!</span>
            </div>

        </div>
      </div>
    </div>
  );
};
